"use client"

// packages/frontend/src/components/ui/reactbits/AuroraWrapper.tsx

import { type ReactNode } from "react"
import { useTheme } from "../../providers/ThemeProvider"
import { cn } from "@/utils/cn"

interface AuroraWrapperProps {
  children: ReactNode
  className?: string
  intensity?: "low" | "medium" | "high"
  disabled?: boolean
}

/**
 * Wrapper for ReactBits Aurora component with theme support
 * Provides dynamic background effects for child workspace pages
 */
export function AuroraWrapper({
  children,
  className,
  intensity = "medium",
  disabled = false,
}: AuroraWrapperProps) {
  const { themeType } = useTheme()
  const isChildTheme = themeType === "child"

  // If disabled or not child theme, return children without background
  if (disabled || !isChildTheme) {
    return <>{children}</>
  }

  // Try to import ReactBits Aurora, fallback to custom implementation
  let Aurora: any = null
  try {
    const reactbits = require("@appletosolutions/reactbits")
    Aurora = reactbits.Aurora || reactbits.default?.Aurora
  } catch (e) {
    // Fallback to custom implementation
  }

  // Custom Aurora effect using CSS gradients
  const intensityClasses = {
    low: "opacity-20",
    medium: "opacity-30",
    high: "opacity-40",
  }

  // If ReactBits component is available, use it
  if (Aurora) {
    return (
      <Aurora className={className} intensity={intensity}>
        {children}
      </Aurora>
    )
  }

  // Custom implementation with CSS gradients
  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Aurora background effect */}
      <div
        className={cn(
          "absolute inset-0 -z-10 pointer-events-none",
          intensityClasses[intensity],
          "bg-linear-to-br from-child-primary/20 via-child-secondary/15 to-child-accent/20",
          "animate-pulse"
        )}
        style={{
          background: `
            radial-gradient(ellipse at top, rgba(135, 206, 235, 0.3), transparent 50%),
            radial-gradient(ellipse at bottom right, rgba(255, 182, 193, 0.2), transparent 50%),
            radial-gradient(ellipse at bottom left, rgba(173, 216, 230, 0.2), transparent 50%)
          `,
        }}
      />
      {children}
    </div>
  )
}

